import React from 'react';
import {Linking, View} from 'react-native';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import config from '../../config';

import ForceUpgradeScreenContent from './ForceUpgradeScreenContent';

/**
 * Open store with deep link + fallback strategy
 * Matches production behavior exactly (useForceUpgradeVersion.ts)
 *
 * Note: storeLinks.ts uses Platform.OS internally, so we construct URLs
 * manually to test cross-platform (e.g., test Play Store from iOS simulator)
 */
const openStoreLink = async (targetPlatform: 'ios' | 'android') => {
  const brandId = config.WHITE_LABEL_ID || 'unknown';
  const appStoreId = config.APP_STORE_ID;
  const packageId = config.PLAY_STORE_PACKAGE;

  let deepLink: string | null = null;
  let fallbackUrl: string | null = null;

  if (targetPlatform === 'ios') {
    if (!appStoreId) {
      console.error(`No App Store ID configured for brand: ${brandId}`);
      return;
    }
    deepLink = `itms-apps://apps.apple.com/app/id${appStoreId}`;
    fallbackUrl = `https://apps.apple.com/au/app/id${appStoreId}`;
  } else {
    if (!packageId) {
      console.error(`No Play Store ID configured for brand: ${brandId}`);
      return;
    }
    deepLink = `market://details?id=${packageId}`;
    fallbackUrl = `https://play.google.com/store/apps/details?id=${packageId}`;
  }

  console.log(`[${brandId}] Opening ${targetPlatform === 'ios' ? 'App' : 'Play'} Store:`, deepLink);

  // Match production behavior: deep link first, fallback on error
  if (deepLink) {
    try {
      await Linking.openURL(deepLink);
    } catch {
      console.log(`Deep link failed, trying fallback:`, fallbackUrl);
      if (fallbackUrl) {
        try {
          await Linking.openURL(fallbackUrl);
        } catch (error) {
          console.error('Failed to open store URL:', error);
        }
      }
    }
  }
};

const meta = {
  title: 'Screens/ForceUpgradeScreen',
  component: ForceUpgradeScreenContent,
  decorators: [
    Story => (
      <View style={{flex: 1, width: '100%', height: 600}}>
        <Story />
      </View>
    ),
  ],
  parameters: {
    layout: 'fullscreen',
    docs: {
      description: {
        component:
          'Full-screen blocking upgrade modal displayed when app version is below minimum required.\n\n' +
          '**Behavior:**\n' +
          '- iOS/Android: Opens respective app store (deep link with HTTPS fallback)\n' +
          '- Web: Refreshes the page\n\n' +
          '**Use the brand selector** to preview different brand themes.\n\n' +
          '**Testing Store Links:** Use the "Test App Store" and "Test Play Store" stories to verify store navigation.',
      },
    },
  },
  args: {
    currentVersion: '1.2.0',
    minimumVersion: '2.5.0',
    onUpdatePress: () => console.log('Update pressed'),
  },
} satisfies Meta<typeof ForceUpgradeScreenContent>;

export default meta;

type Story = StoryObj<typeof meta>;

/**
 * Default state showing the upgrade required modal
 * Simulates: current version 1.2.0 < required version 2.5.0
 */
export const Default: Story = {
  name: 'Upgrade Required',
  args: {
    currentVersion: '1.2.0',
    minimumVersion: '2.5.0',
  },
  parameters: {
    docs: {
      description: {
        story:
          'Default force upgrade screen showing:\n' +
          '- Brand logo header\n' +
          '- Update required message\n' +
          '- Current version: 1.2.0\n' +
          '- Required version: 2.5.0\n' +
          '- Update button (opens store or refreshes)',
      },
    },
  },
};

/**
 * Mobile viewport simulation
 */
export const MobileView: Story = {
  name: 'Mobile Viewport',
  args: {
    currentVersion: '1.0.0',
    minimumVersion: '3.0.0',
  },
  parameters: {
    viewport: {
      defaultViewport: 'mobile1',
    },
    docs: {
      description: {
        story: 'Force upgrade screen as it appears on mobile devices with narrower card width.',
      },
    },
  },
};

/**
 * Minor version upgrade scenario
 */
export const MinorUpgrade: Story = {
  name: 'Minor Version Upgrade',
  args: {
    currentVersion: '2.4.9',
    minimumVersion: '2.5.0',
  },
  parameters: {
    docs: {
      description: {
        story: 'User is just one minor version behind (2.4.9 → 2.5.0).',
      },
    },
  },
};

/**
 * Test App Store navigation (opens in new tab)
 * Click "Update now" to verify the App Store URL is correct for the current brand
 */
export const TestAppStore: Story = {
  name: 'Test App Store Link',
  args: {
    currentVersion: '1.0.0',
    minimumVersion: '2.0.0',
    onUpdatePress: () => openStoreLink('ios'),
  },
  parameters: {
    docs: {
      description: {
        story:
          '**Click "Update now" to test App Store navigation.**\n\n' +
          'Opens the App Store page for the current brand in a new tab.\n' +
          'Check the console for the URL being opened.',
      },
    },
  },
};

/**
 * Test Play Store navigation (opens in new tab)
 * Click "Update now" to verify the Play Store URL is correct for the current brand
 */
export const TestPlayStore: Story = {
  name: 'Test Play Store Link',
  args: {
    currentVersion: '1.0.0',
    minimumVersion: '2.0.0',
    onUpdatePress: () => openStoreLink('android'),
  },
  parameters: {
    docs: {
      description: {
        story:
          '**Click "Update now" to test Play Store navigation.**\n\n' +
          'Opens the Play Store page for the current brand in a new tab.\n' +
          'Check the console for the URL being opened.',
      },
    },
  },
};
